import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { devWarn } from "@/lib/log";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { LogOut, LayoutDashboard, ChevronDown, ShieldCheck, BarChart3, User, ShoppingCart, Users, Package, Wallet, Inbox, Moon, Sun, ClipboardList, Truck, FolderOpen, Factory, Sparkles, MessageCircle, ShoppingBag, Star, Boxes, Recycle } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import Logo from "@/components/Logo";
import BottomNav from "@/components/BottomNav";
import AiChatbot from "@/components/AiChatbot";

export default function AppLayout({ children, sidebar = null }) {
  const { user, logout, hasModule, isPrivileged } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const initial = (user?.name?.[0] || user?.email?.[0] || "U").toUpperCase();
  const [requestCount, setRequestCount] = useState(0);
  const isBusinessUser = !!user;   // module gate does fine-grained control now
  // Only the User Settings tab stays strictly admin-only — everything
  // else (professional dashboards, reports, transporters, trip tracker…)
  // opens up to managers so a promoted trusted operator gets the full
  // control surface without holding the keys to the user table.
  const isAdminOnly = user?.role === "admin";

  // Poll admin/manager's pending shop-request count (light-weight, every 30s)
  useEffect(() => {
    if (!isPrivileged) return undefined;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/order-requests/meta/pending-count");
        if (!cancelled) setRequestCount(data?.count || 0);
      } catch (e) { devWarn("pending-count fetch failed", e); }
    };
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isPrivileged]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F7F7F6] dark:bg-[#0B0D10] flex flex-col">
      <header className="hidden md:block sticky top-0 z-30 glass border-b border-[#0D0D0D]/10 dark:border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 min-h-[72px] py-2 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/dashboard" className="flex items-center" data-testid="brand-link">
              <Logo size={64} textSize="text-lg" />
            </Link>
            <nav className="hidden md:flex items-center gap-0.5 flex-wrap">
              {hasModule("dashboard") && (
                <Link
                  to="/dashboard"
                  data-testid="nav-dashboard-link"
                  className={`px-2.5 py-1.5 text-xs font-medium border ${
                    location.pathname.startsWith("/dashboard")
                      ? "border-[#DC262F] bg-[#DC262F] text-white"
                      : "border-transparent text-[#0D0D0D] dark:text-white/85 hover:border-[#E2E2E2] dark:hover:border-white/15 dark:hover:bg-white/5"
                  }`}
                >
                  <LayoutDashboard className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                  Dashboard
                </Link>
              )}
              <Link
                to="/reports"
                data-testid="nav-reports-link"
                className={`px-2.5 py-1.5 text-xs font-medium border ${
                  location.pathname.startsWith("/reports")
                    ? "border-[#DC262F] bg-[#DC262F] text-white"
                    : "border-transparent text-[#0D0D0D] dark:text-white/85 hover:border-[#E2E2E2] dark:hover:border-white/15 dark:hover:bg-white/5"
                }`}
              >
                <BarChart3 className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                Reports
              </Link>
              <Link
                to="/chat"
                data-testid="nav-chat-link"
                className={`px-2.5 py-1.5 text-xs font-medium border ${
                  location.pathname.startsWith("/chat")
                    ? "border-[#2563EB] bg-[#2563EB] text-white"
                    : "border-transparent text-[#0D0D0D] dark:text-white/85 hover:border-[#E2E2E2] dark:hover:border-white/15 dark:hover:bg-white/5"
                }`}
              >
                <MessageCircle className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                Chat
              </Link>
              {isBusinessUser && hasModule("orders") && (
                <Link
                  to="/orders/new"
                  data-testid="nav-orders-link"
                  className={`px-2.5 py-1.5 text-xs font-medium border ${
                    (location.pathname === "/orders" || location.pathname.startsWith("/orders/"))
                      ? "border-[#DC262F] bg-[#DC262F] text-white"
                      : "border-transparent text-[#0D0D0D] dark:text-white/85 hover:border-[#E2E2E2] dark:hover:border-white/15 dark:hover:bg-white/5"
                  }`}
                >
                  <ShoppingCart className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                  Orders
                </Link>
              )}
              {isPrivileged && hasModule("shop_requests") && (
                <Link
                  to="/order-requests"
                  data-testid="nav-requests-link"
                  className={`px-2.5 py-1.5 text-xs font-medium border relative ${
                    location.pathname.startsWith("/order-requests")
                      ? "border-[#DC262F] bg-[#DC262F] text-white"
                      : "border-transparent text-[#0D0D0D] dark:text-white/85 hover:border-[#E2E2E2] dark:hover:border-white/15 dark:hover:bg-white/5"
                  }`}
                >
                  <Inbox className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                  Requests
                  {requestCount > 0 && (
                    <span data-testid="nav-requests-badge" className="ml-1.5 inline-flex items-center justify-center bg-[#FF3B30] text-white text-[10px] font-black px-1.5 min-w-[1.1rem] h-[1.1rem]">
                      {requestCount > 99 ? "99+" : requestCount}
                    </span>
                  )}
                </Link>
              )}
              {isBusinessUser && hasModule("customers") && (
                <Link
                  to="/customers"
                  data-testid="nav-customers-link"
                  className={`px-2.5 py-1.5 text-xs font-medium border ${
                    location.pathname.startsWith("/customers")
                      ? "border-[#DC262F] bg-[#DC262F] text-white"
                      : "border-transparent text-[#0D0D0D] dark:text-white/85 hover:border-[#E2E2E2] dark:hover:border-white/15 dark:hover:bg-white/5"
                  }`}
                >
                  <Users className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                  Customers
                </Link>
              )}
              {isBusinessUser && hasModule("products") && (
                <Link
                  to="/products"
                  data-testid="nav-products-link"
                  className={`px-2.5 py-1.5 text-xs font-medium border ${
                    location.pathname === "/products"
                      ? "border-[#DC262F] bg-[#DC262F] text-white"
                      : "border-transparent text-[#0D0D0D] dark:text-white/85 hover:border-[#E2E2E2] dark:hover:border-white/15 dark:hover:bg-white/5"
                  }`}
                >
                  <Package className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                  Products
                </Link>
              )}
              {isBusinessUser && hasModule("products") && (
                <Link
                  to="/inventory"
                  data-testid="nav-inventory-link"
                  className={`px-2.5 py-1.5 text-xs font-medium border ${
                    location.pathname.startsWith("/inventory")
                      ? "border-[#DC262F] bg-[#DC262F] text-white"
                      : "border-transparent text-[#0D0D0D] dark:text-white/85 hover:border-[#E2E2E2] dark:hover:border-white/15 dark:hover:bg-white/5"
                  }`}
                >
                  <Boxes className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                  Inventory
                </Link>
              )}
              {isBusinessUser && hasModule("products") && (
                <Link
                  to="/material-logs"
                  data-testid="nav-material-logs-link"
                  className={`px-2.5 py-1.5 text-xs font-medium border ${
                    location.pathname.startsWith("/material-logs")
                      ? "border-[#DC262F] bg-[#DC262F] text-white"
                      : "border-transparent text-[#0D0D0D] dark:text-white/85 hover:border-[#E2E2E2] dark:hover:border-white/15 dark:hover:bg-white/5"
                  }`}
                >
                  <Recycle className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                  Material Production
                </Link>
              )}
              {isBusinessUser && hasModule("expenses") && (
                <Link
                  to="/expenses"
                  data-testid="nav-expenses-link"
                  className={`px-2.5 py-1.5 text-xs font-medium border ${
                    location.pathname.startsWith("/expenses")
                      ? "border-[#DC262F] bg-[#DC262F] text-white"
                      : "border-transparent text-[#0D0D0D] dark:text-white/85 hover:border-[#E2E2E2] dark:hover:border-white/15 dark:hover:bg-white/5"
                  }`}
                >
                  <Wallet className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                  Expenses
                </Link>
              )}
              {isBusinessUser && hasModule("purchase_orders") && (
                <Link
                  to="/purchase-orders"
                  data-testid="nav-purchase-orders-link"
                  className={`px-2.5 py-1.5 text-xs font-medium border ${
                    location.pathname.startsWith("/purchase-orders")
                      ? "border-[#DC262F] bg-[#DC262F] text-white"
                      : "border-transparent text-[#0D0D0D] dark:text-white/85 hover:border-[#E2E2E2] dark:hover:border-white/15 dark:hover:bg-white/5"
                  }`}
                >
                  <ClipboardList className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                  POs
                </Link>
              )}
              {isBusinessUser && (
                <Link
                  to="/suppliers"
                  data-testid="nav-suppliers-link"
                  className={`px-2.5 py-1.5 text-xs font-medium border ${
                    location.pathname.startsWith("/suppliers")
                      ? "border-[#DC262F] bg-[#DC262F] text-white"
                      : "border-transparent text-[#0D0D0D] dark:text-white/85 hover:border-[#E2E2E2] dark:hover:border-white/15 dark:hover:bg-white/5"
                  }`}
                >
                  <Truck className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                  Suppliers
                </Link>
              )}
              {(hasModule("mfg.dashboard") || hasModule("mfg.machines") || hasModule("mfg.moulds") || hasModule("mfg.products") || hasModule("mfg.materials") || hasModule("mfg.shifts") || hasModule("mfg.production") || hasModule("mfg.hourly_log") || hasModule("mfg.reports") || hasModule("mfg.payroll")) && (
                <Link
                  to="/mfg"
                  data-testid="nav-mfg-link"
                  className={`px-2.5 py-1.5 text-xs font-medium border ${
                    location.pathname.startsWith("/mfg")
                      ? "border-[#DC262F] bg-[#DC262F] text-white"
                      : "border-transparent text-[#0D0D0D] dark:text-white/85 hover:border-[#E2E2E2] dark:hover:border-white/15 dark:hover:bg-white/5"
                  }`}
                >
                  <Factory className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                  Mfg
                </Link>
              )}
              {(hasModule("crm.dashboard") || hasModule("crm.leads") || hasModule("crm.pipeline") || hasModule("crm.activities")) && (
                <Link
                  to="/crm"
                  data-testid="nav-crm-link"
                  className={`px-2.5 py-1.5 text-xs font-medium border ${
                    location.pathname.startsWith("/crm")
                      ? "border-[#DC262F] bg-[#DC262F] text-white"
                      : "border-transparent text-[#0D0D0D] dark:text-white/85 hover:border-[#E2E2E2] dark:hover:border-white/15 dark:hover:bg-white/5"
                  }`}
                >
                  <Sparkles className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                  CRM
                </Link>
              )}
            </nav>
          </div>

          <div className="flex items-center gap-2">
            <NotificationBell />
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex items-center gap-2 border border-[#E2E2E2] dark:border-white/10 hover:border-[#DC262F] dark:hover:border-[#DC262F] px-2 py-1.5 rounded-md transition-colors text-[#0D0D0D] dark:text-white/85">
                <div className="w-7 h-7 flex items-center justify-center text-white font-bold text-xs" style={{ background: user?.avatar_color || "#0D0D0D" }}>
                  {initial}
                </div>
                <span className="text-sm font-medium hidden sm:block">{user?.name || user?.email}</span>
                <ChevronDown className="h-3.5 w-3.5 text-neutral-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none border-[#0D0D0D] w-48">
              <div className="px-2 py-2">
                <div className="text-xs font-bold">{user?.name}</div>
                <div className="text-xs text-neutral-500 truncate">{user?.email}</div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={toggleTheme} data-testid="theme-menu-item" className="rounded-none cursor-pointer">
                {theme === "dark" ? <><Sun className="h-4 w-4 mr-2" /> Light mode</> : <><Moon className="h-4 w-4 mr-2" /> Dark mode</>}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="profile-menu-item" className="rounded-none cursor-pointer">
                <User className="h-4 w-4 mr-2" /> Profile & WhatsApp
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/files")} data-testid="files-menu-item" className="rounded-none cursor-pointer">
                <FolderOpen className="h-4 w-4 mr-2" /> Files
              </DropdownMenuItem>
              {isAdminOnly && (
                <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="admin-menu-item" className="rounded-none cursor-pointer">
                  <ShieldCheck className="h-4 w-4 mr-2" /> Admin panel
                </DropdownMenuItem>
              )}
              {isPrivileged && (
                <DropdownMenuItem onClick={() => navigate("/admin/performance")} data-testid="admin-performance-menu-item" className="rounded-none cursor-pointer">
                  <BarChart3 className="h-4 w-4 mr-2" /> Performance Dashboard
                </DropdownMenuItem>
              )}
              {isPrivileged && (
                <DropdownMenuItem onClick={() => navigate("/admin/orders")} data-testid="admin-b2c-orders-menu-item" className="rounded-none cursor-pointer">
                  <ShoppingBag className="h-4 w-4 mr-2" /> B2C Orders
                </DropdownMenuItem>
              )}
              {isPrivileged && (
                <DropdownMenuItem onClick={() => navigate("/admin/reports")} data-testid="admin-reports-menu-item" className="rounded-none cursor-pointer">
                  <BarChart3 className="h-4 w-4 mr-2" /> Accounting Reports
                </DropdownMenuItem>
              )}
              {isPrivileged && (
                <DropdownMenuItem onClick={() => navigate("/admin/transporters")} data-testid="admin-transporters-menu-item" className="rounded-none cursor-pointer">
                  <Truck className="h-4 w-4 mr-2" /> Transporters
                </DropdownMenuItem>
              )}
              {isPrivileged && (
                <DropdownMenuItem onClick={() => navigate("/admin/transporter-ageing")} data-testid="admin-transporter-ageing-menu-item" className="rounded-none cursor-pointer">
                  <Truck className="h-4 w-4 mr-2" /> Transporter Ageing
                </DropdownMenuItem>
              )}
              {isPrivileged && (
                <DropdownMenuItem onClick={() => navigate("/admin/trip-tracker")} data-testid="admin-trip-tracker-menu-item" className="rounded-none cursor-pointer">
                  <Truck className="h-4 w-4 mr-2" /> Live Trip Tracker
                </DropdownMenuItem>
              )}
              {isPrivileged && (
                <DropdownMenuItem onClick={() => navigate("/admin/reviews")} data-testid="admin-reviews-menu-item" className="rounded-none cursor-pointer">
                  <Star className="h-4 w-4 mr-2" /> Customer Reviews
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item" className="rounded-none cursor-pointer">
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile top bar — logo left, notif + theme right */}
      <header className="md:hidden sticky top-0 z-30 glass px-4 py-2 flex items-center justify-between pt-[calc(env(safe-area-inset-top)+0.5rem)]">
        <Link to="/dashboard" data-testid="brand-link-mobile" className="flex items-center">
          <Logo size={40} textSize="text-sm" />
        </Link>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={toggleTheme}
            data-testid="theme-toggle-mobile"
            className="h-10 w-10 flex items-center justify-center text-black dark:text-white active:bg-neutral-100 dark:active:bg-neutral-900"
            aria-label="Toggle dark mode"
          >
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <NotificationBell />
        </div>
      </header>

      <div className="flex-1 flex pb-16 md:pb-0">
        {sidebar && (
          <aside className="hidden lg:block w-64 border-r border-[#E2E2E2] dark:border-white/5 glass">
            {sidebar}
          </aside>
        )}
        <main className="flex-1 min-w-0">{children}</main>
      </div>

      <BottomNav />
      <AiChatbot />
    </div>
  );
}
